import { FEEDBACK_FILTER_VALUE } from '@/constants/bot';
import { FC, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { EyeOff, X, ShieldAlert } from 'lucide-react';
import { ProductsContext } from '../context';
import { useContact } from '@/hooks/useContact';
import {
  useJoinThread,
  useLeaveThread,
  useRequestForm,
  useSendSDRMessage,
  useGetInboxStatus,
  useGetWhispers,
  useCreateWhisper,
  usePingWatcherPresence,
  useRemoveWatcherPresence,
  useRestoreThreadFromSpam,
} from '@/hooks/useBot';
import { useUserContext } from '@/contexts/UserProvider';
import { useAuth } from '@/contexts/AuthProvider';
import MessagesContainer from './MessagesContainer';
import ThreadDetailsHeader from './ThreadDetailsHeader';
import { Inbox, Send } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { socketIo } from '@/contexts/NotificationProvider';

import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { isHighConfidenceSpam } from '../lib/spamDisposition';

interface IThreadDetailsProps {
  // Real-time visitor presence — driven by MessagesHub's liveThreadIds set
  // (which is updated by bot:visitor_active / bot:visitor_message socket
  // events and cleared by bot:visitor_offline). Distinct from
  // `activeThread.lastInteractionAt`, which is just the time of the last
  // message and does not reflect whether the visitor's tab is currently open.
  isVisitorLive?: boolean;
  isDone?: boolean;
  isUpdatingDisposition?: boolean;
  onToggleDone?: () => void;
  onOpenContactPanel?: () => void;
}

const ThreadDetails: FC<IThreadDetailsProps> = ({
  isVisitorLive,
  isDone = false,
  isUpdatingDisposition = false,
  onToggleDone,
  onOpenContactPanel,
}) => {
  const {
    activeThread,
    setActiveThread,
    threadInteractions,
    setThreadInteractions,
    setThreads,
    feedbacksFilter,
  } = useContext(ProductsContext);

  const { askIOTUserDetails } = useUserContext();
  const { user: authUser } = useAuth();

  const threadId = activeThread?.threadId || null;
  const { data: contactData } = useContact(threadId);
  const { data: inboxStatus } = useGetInboxStatus(threadId);
  const { data: whisperData } = useGetWhispers(threadId);

  const [messageInput, setMessageInput] = useState('');
  const [whisperInput, setWhisperInput] = useState('');
  const [isWhisperModalOpen, setIsWhisperModalOpen] = useState(false);
  const [isSDRActive, setIsSDRActive] = useState(false);
  const [whispers, setWhispers] = useState<Array<{
    id: string;
    author_name: string;
    body: string;
    created_at: string;
  }>>([]);
  const [watchers, setWatchers] = useState<Array<{ userId: string; name: string }>>([]);
  const prevThreadIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  const { mutate: joinThread, isPending: isJoining } = useJoinThread();
  const { mutate: leaveThread, isPending: isLeaving } = useLeaveThread();
  const { mutate: requestForm, isPending: isSendingForm } = useRequestForm();
  const { mutate: sendSDRMessage, isPending: isSending } = useSendSDRMessage();
  const { mutate: createWhisper, isPending: isWhisperSending } = useCreateWhisper();
  const { mutate: pingWatcherPresence } = usePingWatcherPresence();
  const { mutate: removeWatcherPresence } = useRemoveWatcherPresence();
  const { mutate: restoreThreadFromSpam, isPending: isRestoringSpam } = useRestoreThreadFromSpam();

  const isSpam = isHighConfidenceSpam(activeThread?.spamDisposition);

  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when thread interactions change or when switching threads
  useEffect(() => {
    scrollToBottom();
  }, [threadInteractions, threadId]);

  // Reset SDR active state when thread switches, before status loads
  useEffect(() => {
    if (threadId !== prevThreadIdRef.current) {
      prevThreadIdRef.current = threadId;
      setIsSDRActive(false);
    }
  }, [threadId]);

  // Restore SDR active state from backend after thread switch / page refresh
  useEffect(() => {
    if (!inboxStatus) return;
    if (inboxStatus.active) {
      const myUserId = askIOTUserDetails?.user_id || authUser?.id;
      const myEmail = authUser?.email;
      const myFullName = askIOTUserDetails
        ? `${askIOTUserDetails.first_name || ''} ${askIOTUserDetails.last_name || ''}`.trim()
        : null;
      const isMySession =
        (myUserId && inboxStatus.joinedById === myUserId) ||
        (myEmail && inboxStatus.joinedBy === myEmail) ||
        (myFullName && inboxStatus.joinedBy === myFullName);
      setIsSDRActive(!!isMySession);
    } else {
      setIsSDRActive(false);
    }
  }, [inboxStatus, authUser, askIOTUserDetails]);

  useEffect(() => {
    setWhispers(whisperData?.whispers || []);
  }, [whisperData]);

  // Show visitor messages in real-time as they arrive from the socket
  useEffect(() => {
    if (!threadId) return;

    const handleVisitorMessage = (data: { threadId: string; query: string; timestamp: string }) => {
      if (data.threadId !== threadId) return;
      setThreadInteractions((prev) => {
        const isDupe = prev.some(
          (i) => i.user === data.query && i.timestamp === data.timestamp,
        );
        if (isDupe) return prev;
        return [
          ...prev,
          {
            id: Date.now(),
            ai: '',
            user: data.query,
            keywords: '',
            timestamp: data.timestamp,
          },
        ];
      });
    };

    const handleWhisper = (data: { threadId: string; whisper: any }) => {
      if (data.threadId !== threadId || !data.whisper) return;
      setWhispers((prev) => {
        if (prev.some((w) => w.id === data.whisper.id)) return prev;
        return [...prev, data.whisper];
      });
    };

    const handleWatcherPresence = (data: {
      threadId: string;
      watchers: Array<{ userId: string; name: string }>;
    }) => {
      if (data.threadId !== threadId) return;
      setWatchers(Array.isArray(data.watchers) ? data.watchers : []);
    };

    const invalidateInboxStatus = (data: { threadId: string }) => {
      if (data.threadId !== threadId) return;
      queryClient.invalidateQueries({
        queryKey: [threadId ? API_ENDPOINTS.inboxStatus(threadId) : 'inbox-status-null', threadId],
        exact: false,
      });
    };

    const injectSystemMessage = (data: { threadId: string; sdrName?: string }, eventType: string) => {
      if (data.threadId !== threadId || !data.sdrName) return;
      const event = eventType === 'joined' ? 'agent_joined' : 'agent_left';
      const text = eventType === 'joined'
        ? `${data.sdrName} joined the conversation.`
        : `${data.sdrName} left the conversation. AI resumed.`;
      setThreadInteractions((prev) => {
        const isDupe = prev.some((i) => i.event === event && i.ai === text);
        if (isDupe) return prev;
        return [...prev, { id: Date.now(), ai: text, user: '', keywords: '', event, timestamp: new Date().toISOString() }];
      });
    };

    const handleHumanJoined = (data: { threadId: string; sdrName?: string }) => {
      invalidateInboxStatus(data);
      injectSystemMessage(data, 'joined');
    };

    const handleHumanLeft = (data: { threadId: string; sdrName?: string }) => {
      invalidateInboxStatus(data);
      injectSystemMessage(data, 'left');
    };

    // Agent-triggered contact-form request (Teams/Slack "Send Form" button or dashboard
    // equivalent) — without this, the request writes to the session server-side but the
    // currently-open thread never shows it until the dashboard is manually refreshed.
    const handleFormRequested = (data: { threadId: string; timestamp?: string }) => {
      if (data.threadId !== threadId) return;
      const timestamp = data.timestamp || new Date().toISOString();
      setThreadInteractions((prev) => {
        const isDupe = prev.some((i) => i.event === 'form_requested' && i.timestamp === timestamp);
        if (isDupe) return prev;
        return [...prev, {
          id: Date.now(), ai: '', user: '', keywords: '', event: 'form_requested', timestamp,
        }];
      });
    };

    // Receive SDR messages sent by a teammate — skip own messages (already shown via optimistic update).
    const handleSdrMessage = (data: { threadId: string; sdrName: string; senderUserId: string; message: string; timestamp: string }) => {
      if (data.threadId !== threadId) return;
      const myUserId = authUser?.id;
      if (myUserId && data.senderUserId === myUserId) return;
      setThreadInteractions((prev) => {
        const isDupe = prev.some((i) => i.ai === data.message && i.timestamp === data.timestamp);
        if (isDupe) return prev;
        return [
          ...prev,
          {
            id: Date.now(),
            ai: data.message,
            user: '',
            keywords: '',
            event: 'human_message',
            sender: data.sdrName,
            timestamp: data.timestamp,
          },
        ];
      });
    };

    socketIo.on('bot:visitor_message', handleVisitorMessage);
    socketIo.on('thread:whisper-new', handleWhisper);
    socketIo.on('thread:watcher-presence', handleWatcherPresence);
    socketIo.on('thread:human-joined', handleHumanJoined);
    socketIo.on('thread:human-left', handleHumanLeft);
    socketIo.on('thread:form-requested', handleFormRequested);
    socketIo.on('thread:sdr-message', handleSdrMessage);
    return () => {
      socketIo.off('bot:visitor_message', handleVisitorMessage);
      socketIo.off('thread:whisper-new', handleWhisper);
      socketIo.off('thread:watcher-presence', handleWatcherPresence);
      socketIo.off('thread:human-joined', handleHumanJoined);
      socketIo.off('thread:human-left', handleHumanLeft);
      socketIo.off('thread:form-requested', handleFormRequested);
      socketIo.off('thread:sdr-message', handleSdrMessage);
    };
  }, [threadId, setThreadInteractions, queryClient, authUser?.id]);

  useEffect(() => {
    if (!threadId) return undefined;

    pingWatcherPresence(threadId);
    const interval = setInterval(() => {
      pingWatcherPresence(threadId);
    }, 20_000);

    return () => {
      clearInterval(interval);
      removeWatcherPresence(threadId);
    };
  }, [threadId, pingWatcherPresence, removeWatcherPresence]);

  const filteredThreadsInteraction = useMemo(() => {
    return threadInteractions.filter((item) =>
      feedbacksFilter.some(
        (feedback) => feedback === FEEDBACK_FILTER_VALUE.ALL || Boolean(feedback) === item.feedback,
      ),
    );
  }, [feedbacksFilter, threadInteractions]);

  const displayName = useMemo(() => {
    if (contactData?.contactInfo?.fullName) return contactData.contactInfo.fullName;
    if (contactData?.contactInfo?.company) return contactData.contactInfo.company;
    if (activeThread?.companyInfo?.name) return activeThread.companyInfo.name;
    if (activeThread?.userEmail) return activeThread.userEmail;
    if (contactData?.contactInfo?.email) return contactData.contactInfo.email;
    return 'Unknown Visitor';
  }, [contactData, activeThread]);

  const visitorWebsite = useMemo(() => {
    return contactData?.contactInfo?.website || activeThread?.companyInfo?.domain || null;
  }, [contactData, activeThread]);

  // Optimistic-render label for the SDR's own messages. Prefer real names from
  // Supabase user_metadata (where SSO/profile data lives) before falling back
  // to the local backend profile fetched via UserProvider; only use the email
  // address as a last resort so the conversation doesn't render
  // "A.ASHARI1302@GMAIL.COM · HUMAN" when we already know the user's name.
  const sdrDisplayName = (() => {
    const fromMetadata = (authUser?.user_metadata?.full_name as string)
      || [authUser?.user_metadata?.first_name, authUser?.user_metadata?.last_name].filter(Boolean).join(' ').trim()
      || (authUser?.user_metadata?.name as string);
    if (fromMetadata && !fromMetadata.includes('@')) return fromMetadata.trim();
    const fromProfile = askIOTUserDetails
      ? `${askIOTUserDetails.first_name || ''} ${askIOTUserDetails.last_name || ''}`.trim()
      : '';
    if (fromProfile) return fromProfile;
    // Last resort: derive a readable label from the email local part (strip
    // dots/numbers) instead of dumping the full email into the message.
    const emailLocal = (authUser?.email || '').split('@')[0];
    if (emailLocal) {
      const cleaned = emailLocal.replace(/[._-]+/g, ' ').replace(/\d+/g, '').trim();
      if (cleaned) {
        return cleaned
          .split(/\s+/)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ');
      }
    }
    return 'Agent';
  })();

  const handleJoin = () => {
    if (!threadId) return;
    if (isSpam) {
      toast.info('Restore this conversation before joining it');
      return;
    }
    joinThread(threadId, {
      onSuccess: () => {
        setIsSDRActive(true);
        toast.success('You joined the conversation');
      },
      onError: (err: any) => {
        const joinedBy = err?.response?.data?.joined_by;
        if (err?.response?.status === 409) {
          toast.error(
            joinedBy
              ? `${joinedBy} has already joined this conversation`
              : 'Someone else has already joined this conversation',
          );
        } else {
          toast.error('Failed to join conversation');
        }
      },
    });
  };

  const handleRestoreFromSpam = () => {
    if (!threadId) return;
    restoreThreadFromSpam(threadId, {
      onSuccess: () => {
        setThreads((current) => current.filter((thread) => thread.threadId !== threadId));
        setActiveThread(undefined);
        setThreadInteractions([]);
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getThreadDetails], exact: false });
        toast.success('Conversation restored to the inbox');
      },
      onError: () => toast.error('Failed to restore conversation'),
    });
  };

  const handleLeave = () => {
    if (!threadId) return;
    leaveThread(threadId, {
      onSuccess: () => {
        setIsSDRActive(false);
        toast.success('Handed back to AI');
      },
      onError: () => {
        toast.error('Failed to leave conversation');
      },
    });
  };

  const handleSendForm = () => {
    if (!threadId) return;
    requestForm(threadId, {
      onSuccess: (data) => {
        if (data?.sent === false && data?.reason === 'already_collected') {
          toast.info("This visitor's contact info has already been collected");
        } else if (data?.sent === false && data?.reason === 'already_pending') {
          toast.info('The contact form is already showing for this visitor');
        } else {
          toast.success('Contact form sent to the visitor');
        }
      },
      onError: (err: any) => {
        if (err?.response?.status === 403) {
          toast.error("This conversation doesn't belong to your workspace");
        } else {
          toast.error('Failed to send contact form');
        }
      },
    });
  };

  const handleSendMessage = () => {
    if (!threadId || !messageInput.trim() || !isSDRActive) return;
    const message = messageInput.trim();
    setMessageInput('');

    // Optimistically append to center panel so SDR sees their own messages immediately
    const optimisticId = Date.now();
    const optimisticTs = new Date().toISOString();
    const optimisticInteraction = {
      id: optimisticId,
      ai: `<b>${sdrDisplayName}</b>: ${message}`,
      user: '',
      keywords: '',
      event: 'human_message',
      sender: sdrDisplayName,
      timestamp: optimisticTs,
    };
    setThreadInteractions((prev) => [...prev, optimisticInteraction]);

    // Also update the threads cache so that switching threads and coming back
    // preserves the message (otherwise activeThread.interactions resets to stale API data).
    setThreads((prev) => {
      const idx = prev.findIndex((t) => t.threadId === threadId);
      if (idx === -1) return prev;
      const updated = [...prev];
      const thread = { ...updated[idx] };
      thread.interactions = [...(thread.interactions || []), optimisticInteraction];
      thread.date = optimisticTs;
      thread.lastInteractionAt = optimisticTs;
      updated[idx] = thread;
      return updated;
    });

    sendSDRMessage(
      { threadId, message },
      {
        onError: () => {
          setMessageInput(message);
          setThreadInteractions((prev) => prev.filter((i) => i.id !== optimisticId));
          setThreads((prev) => prev.map((t) => t.threadId === threadId
            ? { ...t, interactions: (t.interactions || []).filter((i) => i.id !== optimisticId) }
            : t));
          toast.error('Failed to send message');
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendWhisper = useCallback(() => {
    if (!threadId || !whisperInput.trim()) return;
    const message = whisperInput.trim();
    setWhisperInput('');
    createWhisper(
      { threadId, message },
      {
        onSuccess: () => {
          setIsWhisperModalOpen(false);
          toast.success('Internal note sent');
        },
        onError: () => {
          setWhisperInput(message);
          toast.error('Failed to send internal note');
        },
      },
    );
  }, [threadId, whisperInput, createWhisper]);

  // Tick every 5s so the AI thinking indicator naturally clears itself if a
  // pending row gets stranded (AI errored out without writing ai). Without
  // this tick the useMemo can't observe time passing.
  const [pendingClock, setPendingClock] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPendingClock((c) => c + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // Detect whether an external (Slack/Teams) agent is actively in the
  // conversation. inboxStatus only tracks in-app SDR sessions, so without this
  // signal the AI typing indicator flashes back on every time the visitor types
  // during a Slack/Teams handoff. Walk the interactions backwards: handoff is
  // active if we see an agent_joined / salesperson_joined event with no later
  // agent_left / agent_auto_left (and no human_chat_closed).
  const isExternalAgentActive = useMemo(() => {
    for (let i = filteredThreadsInteraction.length - 1; i >= 0; i--) {
      const ev = filteredThreadsInteraction[i]?.event;
      if (!ev) continue;
      if (ev === 'agent_left' || ev === 'agent_auto_left' || ev === 'human_chat_closed') {
        return false;
      }
      if (ev === 'agent_joined' || ev === 'salesperson_joined' || ev === 'human_message') {
        return true;
      }
    }
    return false;
  }, [filteredThreadsInteraction]);

  // Show an AI "thinking..." indicator while a pending interaction (user query
  // persisted with ai:null by the backend pre-write) is waiting for the AI.
  // Suppress if any SDR has joined — they handle the reply, not AI. Phase 2
  // currently takes 10-30s for some queries so the SDR needs a visual signal.
  const isAIPending = useMemo(() => {
    const last = filteredThreadsInteraction[filteredThreadsInteraction.length - 1];
    if (!last) return false;
    if (!last.user || last.ai || last.event) return false;
    // Suppress while either the local SDR join state OR the server-side
    // inboxStatus says someone is responding. Checking both because
    // inboxStatus can be undefined during the brief refetch window right
    // after a join — otherwise the indicator flashes back on for a moment.
    // Also suppress for Slack/Teams handoffs — AI is not going to respond
    // when an external agent is actively in the conversation.
    if (isSDRActive || inboxStatus?.active || isExternalAgentActive) return false;
    if (!last.timestamp) return true;
    const ageMs = Date.now() - new Date(last.timestamp).getTime();
    return ageMs < 60_000;
  // pendingClock is intentional — drives re-evaluation as time passes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredThreadsInteraction, isSDRActive, inboxStatus?.active, isExternalAgentActive, pendingClock]);

  const watcherPresenceText = useMemo(() => {
    if (!watchers.length) return null;
    const me = askIOTUserDetails?.user_id || authUser?.id;
    const others = watchers.filter((w) => w.userId !== me);
    if (others.length === 0) return 'You are watching';
    const names = others.map((w) => w.name || 'Teammate');
    const joined = names.slice(0, 2).join(', ');
    const suffix = names.length > 2 ? ` +${names.length - 2} more` : '';
    return `${joined} watching${suffix}`;
  }, [watchers, askIOTUserDetails, authUser]);

  const isHumanRequested = useMemo(() => {
    for (let index = threadInteractions.length - 1; index >= 0; index -= 1) {
      const event = threadInteractions[index]?.event;
      if (event === 'agent_joined' || event === 'salesperson_joined' || event === 'human_message' || event === 'human_chat_closed') {
        return false;
      }
      if (event === 'transfer_human') return true;
    }
    return false;
  }, [threadInteractions]);

  return (
    <div className="h-full w-full bg-white flex flex-col">
      {/* Whisper modal */}
      {isWhisperModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setIsWhisperModalOpen(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-amber-100 bg-amber-50/80 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <EyeOff className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-[14px] font-bold text-slate-900 leading-tight">Internal Note</h3>
                  <p className="text-[11px] text-amber-700 font-medium">Only your team sees this · not visible to visitor</p>
                </div>
              </div>
              <button
                onClick={() => setIsWhisperModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-5 pb-3">
              <textarea
                value={whisperInput}
                onChange={(e) => setWhisperInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSendWhisper(); }
                  if (e.key === 'Escape') setIsWhisperModalOpen(false);
                }}
                placeholder="Write an internal note for your teammates…"
                rows={4}
                className="w-full resize-none rounded-xl border border-amber-200 bg-amber-50/40 px-4 py-3 text-[13px] text-slate-800 placeholder:text-amber-700/50 outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 transition-all"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <p className="text-[10px] text-slate-400 mt-1.5">⌘ Enter to send</p>
            </div>

            <div className="px-6 pb-5 flex items-center justify-end gap-2.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsWhisperModalOpen(false); setWhisperInput(''); }}
                className="text-slate-500 hover:text-slate-700 text-xs"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSendWhisper}
                disabled={!whisperInput.trim() || isWhisperSending}
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-sm"
              >
                <EyeOff className="w-3.5 h-3.5 mr-1.5" />
                {isWhisperSending ? 'Sending…' : 'Send note'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeThread && (
        <ThreadDetailsHeader
          activeThread={activeThread}
          displayName={displayName}
          visitorWebsite={visitorWebsite}
          presenceText={isSDRActive ? null : watcherPresenceText}
          isSDRActive={isSDRActive}
          isVisitorLive={!!isVisitorLive}
          isConversationActive={isSDRActive || !!inboxStatus?.active}
          joinedBy={inboxStatus?.joinedBy}
          isHumanRequested={isHumanRequested}
          isSpam={isSpam}
          isJoining={isJoining}
          isLeaving={isLeaving}
          onJoin={handleJoin}
          onLeave={handleLeave}
          onWhisper={threadId ? () => setIsWhisperModalOpen(true) : undefined}
          onSendForm={threadId ? handleSendForm : undefined}
          isSendingForm={isSendingForm}
          isDone={isDone}
          isUpdatingDisposition={isUpdatingDisposition}
          onToggleDone={onToggleDone}
          onOpenContactPanel={onOpenContactPanel}
        />
      )}

      {activeThread ? (
        <>
          <div className="flex-1 flex flex-col min-h-0">
            {/* Dynamic State Banner */}
            {isSpam ? (
              <div className="mx-5 mt-4 flex items-center justify-between gap-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                <div className="flex items-start gap-3 min-w-0">
                  <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                  <div>
                    <p className="text-xs font-bold text-red-900">AI-detected spam</p>
                    <p className="mt-0.5 text-[11px] text-red-700">
                      Hidden from the inbox and excluded from human-handoff notifications.
                    </p>
                    {activeThread?.spamDisposition?.reasonCode && (
                      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-red-600">
                        Reason: {activeThread.spamDisposition.reasonCode.replaceAll('_', ' ')}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRestoreFromSpam}
                  disabled={isRestoringSpam}
                  className="flex-shrink-0 border-red-300 bg-white text-xs font-bold text-red-700 hover:bg-red-100"
                >
                  {isRestoringSpam ? 'Restoring…' : 'Not spam'}
                </Button>
              </div>
            ) : null}

            <div ref={scrollRef} className="flex-1 overflow-auto px-6 py-7">
              <div className="mx-auto w-full max-w-[720px]">
                <MessagesContainer
                  data={filteredThreadsInteraction}
                  whispers={whispers}
                  isLoading={isAIPending}
                  visitorName={displayName}
                />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white px-6 pb-3 pt-3.5">
            <div className={cn(
              "mx-auto flex w-full max-w-[720px] items-center gap-2 rounded-xl border py-1 pl-3.5 pr-1 transition-colors",
              isSDRActive
                ? 'border-slate-300 bg-white'
                : 'border-slate-200 bg-slate-50'
            )}>
              <input
                type="text"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isSDRActive ? 'Type a message…' : 'Join chat to respond…'}
                disabled={!isSDRActive}
                className="h-[34px] min-w-0 flex-1 bg-transparent text-[13.5px] text-slate-900 placeholder:text-slate-400 outline-none disabled:cursor-not-allowed"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              
              <button
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || isSending || !isSDRActive}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
                  isSDRActive && messageInput.trim()
                    ? "bg-[#0848E7] text-white hover:bg-[#0639BA]"
                    : "cursor-not-allowed bg-transparent text-slate-300"
                )}
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            {!isSDRActive && !isSpam && (
              <p className="mt-2 text-center text-[12px] text-slate-400">
                The AI is handling this conversation — join to take over
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2.5 bg-white p-8 text-slate-400">
          <Inbox className="h-7 w-7 text-slate-300" strokeWidth={1.5} />
          <p className="text-[13px] font-medium">No conversation selected</p>
        </div>
      )}
    </div>
  );
};

export default ThreadDetails;
